import './App.css';
import { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import { collection, addDoc, onSnapshot, query, orderBy, where, serverTimestamp, doc, updateDoc } from 'firebase/firestore';

function App() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [currentChannel, setCurrentChannel] = useState('general');
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [tempUsername, setTempUsername] = useState('');
  const [showNameModal, setShowNameModal] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);

  const channels = ['general', 'random', '開発'];
  const reactionEmojis = ['👍', '❤️', '😂', '🎉', '🔥'];

  useEffect(() => {
    const savedUsername = localStorage.getItem('slack-username');
    if (savedUsername) {
      setUsername(savedUsername);
    } else {
      setShowNameModal(true);
    }
  }, []);

  useEffect(() => {
    if (!username) return;
    
    setLoading(true);
    
    const q = query(
      collection(db, 'messages'),
      where('channel', '==', currentChannel),
      orderBy('timestamp', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newMessages = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        newMessages.push({
          id: doc.id,
          user: data.user,
          text: data.text,
          time: data.time,
          channel: data.channel,
          reactions: data.reactions || {}
        });
      });
      setMessages(newMessages);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentChannel, username]);

  const saveUsername = () => {
    if (tempUsername.trim() === '') {
      alert('名前を入力してください');
      return;
    }
    localStorage.setItem('slack-username', tempUsername);
    setUsername(tempUsername);
    setShowNameModal(false);
  };

  const changeUsername = () => {
    setTempUsername(username);
    setShowNameModal(true);
  };

  const sendMessage = async () => {
    if (inputText.trim() === '') return;
    
    const now = new Date();
    const time = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    try {
      await addDoc(collection(db, 'messages'), {
        user: username,
        text: inputText,
        time: time,
        channel: currentChannel,
        reactions: {},
        timestamp: serverTimestamp()
      });
      
      setInputText('');
    } catch (error) {
      console.error('送信エラー:', error);
    }
  };

  const addReaction = async (messageId, emoji) => {
    try {
      const messageRef = doc(db, 'messages', messageId);
      const message = messages.find(m => m.id === messageId);
      
      if (!message) return;

      const reactions = { ...message.reactions };
      
      if (reactions[emoji]) {
        if (reactions[emoji].includes(username)) {
          reactions[emoji] = reactions[emoji].filter(u => u !== username);
          if (reactions[emoji].length === 0) {
            delete reactions[emoji];
          }
        } else {
          reactions[emoji].push(username);
        }
      } else {
        reactions[emoji] = [username];
      }

      await updateDoc(messageRef, { reactions });
    } catch (error) {
      console.error('リアクションエラー:', error);
    }
  };

  if (showNameModal) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8f8f8'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxWidth: '400px',
          width: '90%'
        }}>
          <h2 style={{ marginBottom: '10px', color: '#1d1c1d' }}>
            💬 My Slackへようこそ
          </h2>
          <p style={{ color: '#666', marginBottom: '25px', fontSize: '14px' }}>
            まずはあなたの名前を教えてください
          </p>
          <input
            type="text"
            value={tempUsername}
            onChange={(e) => setTempUsername(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && saveUsername()}
            placeholder="名前を入力..."
            autoFocus
            style={{
              width: '100%',
              padding: '12px 15px',
              marginBottom: '20px',
              borderRadius: '8px',
              border: '2px solid #ddd',
              fontSize: '16px',
              boxSizing: 'border-box',
              outline: 'none'
            }}
          />
          <button
            onClick={saveUsername}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: '#007a5a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            チャットを始める
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div style={{ fontSize: '20px' }}>読み込み中... 🔥</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'sans-serif' }}>
      
      {/* サイドバー */}
      <div style={{ 
        width: '250px', 
        backgroundColor: '#3f0e40', 
        color: 'white',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <h2 style={{ marginBottom: '20px' }}>💬 My Slack</h2>
        
        <div style={{ marginBottom: '15px', flex: 1 }}>
          <div style={{ 
            fontSize: '12px', 
            fontWeight: 'bold', 
            marginBottom: '10px',
            opacity: 0.7
          }}>
            チャンネル
          </div>
          {channels.map(channel => (
            <div 
              key={channel}
              onClick={() => setCurrentChannel(channel)}
              style={{ 
                padding: '8px 12px',
                marginBottom: '5px',
                borderRadius: '5px',
                cursor: 'pointer',
                backgroundColor: currentChannel === channel ? '#1264a3' : 'transparent',
                transition: 'background-color 0.2s',
                fontWeight: currentChannel === channel ? 'bold' : 'normal'
              }}
            >
              # {channel}
            </div>
          ))}
        </div>

        {/* ユーザー情報 */}
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.2)',
          paddingTop: '15px',
          marginTop: 'auto'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                🟢 {username}
              </div>
              <div style={{ fontSize: '11px', opacity: 0.7 }}>
                オンライン
              </div>
            </div>
            <button
              onClick={changeUsername}
              style={{
                background: 'none',
                border: '1px solid rgba(255,255,255,0.3)',
                color: 'white',
                padding: '5px 10px',
                borderRadius: '5px',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              変更
            </button>
          </div>
        </div>
      </div>

      {/* メインエリア */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        
        {/* ヘッダー */}
        <div style={{ 
          padding: '20px',
          borderBottom: '1px solid #ddd',
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: 0 }}>
            # {currentChannel} 
            <span style={{ fontSize: '14px', color: '#00c853', marginLeft: '10px' }}>
              ⚡ リアルタイム接続中
            </span>
          </h2>
          <p style={{ margin: '5px 0 0 0', fontSize: '13px', color: '#666' }}>
            {messages.length}件のメッセージ
          </p>
        </div>

        {/* メッセージエリア */}
        <div style={{ 
          flex: 1,
          padding: '20px',
          overflowY: 'auto',
          backgroundColor: '#f8f8f8'
        }}>
          {messages.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              color: '#999' 
            }}>
              まだメッセージがありません。<br />
              最初のメッセージを送信してみましょう！
            </div>
          ) : (
            messages.map(msg => (
              <div 
                key={msg.id} 
                style={{ 
                  marginBottom: '20px',
                  position: 'relative'
                }}
              >
                <div
                  onMouseEnter={() => setHoveredMessageId(msg.id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                  style={{ 
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                >
                  <div style={{ marginBottom: '5px' }}>
                    <strong style={{ 
                      color: msg.user === username ? '#e91e63' : '#1264a3',
                      marginRight: '10px' 
                    }}>
                      {msg.user}
                      {msg.user === username && ' (あなた)'}
                    </strong>
                    <span style={{ fontSize: '12px', color: '#888' }}>
                      {msg.time}
                    </span>
                  </div>
                  <div style={{ color: '#1d1c1d', marginBottom: '8px' }}>
                    {msg.text}
                  </div>

                  {/* リアクション表示 */}
                  {Object.keys(msg.reactions).length > 0 && (
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {Object.entries(msg.reactions).map(([emoji, users]) => (
                        <button
                          key={emoji}
                          onClick={() => addReaction(msg.id, emoji)}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: users.includes(username) ? '#e3f2fd' : '#f5f5f5',
                            border: users.includes(username) ? '2px solid #1976d2' : '1px solid #ddd',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title={users.join(', ')}
                        >
                          <span>{emoji}</span>
                          <span style={{ fontSize: '12px', color: '#666' }}>{users.length}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* リアクションボタン（ホバー時表示） */}
                {hoveredMessageId === msg.id && (
                  <div 
                    onMouseEnter={() => setHoveredMessageId(msg.id)}
                    style={{
                      position: 'absolute',
                      top: '-12px',
                      right: '10px',
                      backgroundColor: 'white',
                      borderRadius: '20px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      padding: '5px 8px',
                      display: 'flex',
                      gap: '5px',
                      zIndex: 10
                    }}
                  >
                    {reactionEmojis.map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => addReaction(msg.id, emoji)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '18px',
                          cursor: 'pointer',
                          padding: '2px 5px',
                          borderRadius: '5px',
                          transition: 'transform 0.1s'
                        }}
                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.3)'}
                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* 入力エリア */}
        <div style={{ 
          padding: '20px',
          backgroundColor: 'white',
          borderTop: '1px solid #ddd'
        }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input 
              type="text" 
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder={`${username} として # ${currentChannel} にメッセージを送信`}
              style={{
                flex: 1,
                padding: '12px 15px',
                borderRadius: '8px',
                border: '2px solid #ddd',
                fontSize: '15px',
                outline: 'none'
              }}
            />
            <button 
              onClick={sendMessage}
              style={{
                padding: '12px 25px',
                backgroundColor: '#007a5a',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '15px'
              }}
            >
              送信
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default App;